// maths.portll.net — "This week" hero: one gentle, startable suggestion.
//
// Nudges a single mission — preferring a strand/descriptor the family hasn't
// explored yet, then the shortest one — and offers three low-pressure actions:
// print the recording sheet, commit ("We'll do this" = mark it Planned + open it),
// or reshuffle ("Show another"). Nothing here is a deadline; the framing is
// "explore together", never "assess". comingSoon + already-done tasks are never
// suggested.

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Button, Note, StrandBadge, STRAND_META, injectStyles } from "./ui.jsx";

/**
 * Rank the startable tasks best-first: a fresh strand (weight 2) and/or a fresh
 * descriptor (weight 1) come first, then the shortest, then title (stable).
 * Excludes comingSoon and anything already completed.
 */
export function rankSuggestions(tasks = [], completions = []) {
  const doneIds = new Set(completions.map((c) => c && c.taskId).filter(Boolean));
  const doneTasks = tasks.filter((t) => doneIds.has(t.id));
  const doneStrands = new Set(doneTasks.map((t) => t.strand));
  const doneDescriptors = new Set();
  doneTasks.forEach((t) => (t.descriptorCodes || []).forEach((c) => doneDescriptors.add(c)));

  const novelty = (t) => {
    const strandFresh = doneStrands.has(t.strand) ? 0 : 2;
    const descFresh = (t.descriptorCodes || []).some((c) => !doneDescriptors.has(c)) ? 1 : 0;
    return strandFresh + descFresh;
  };

  return tasks
    .filter((t) => !t.comingSoon && !doneIds.has(t.id))
    .map((t) => ({ t, n: novelty(t) }))
    .sort(
      (a, b) =>
        b.n - a.n ||
        (a.t.estimatedMinutes ?? 999) - (b.t.estimatedMinutes ?? 999) ||
        String(a.t.title || a.t.id).localeCompare(String(b.t.title || b.t.id))
    )
    .map((x) => x.t);
}

export default function ThisWeekCard({ childId, tasks = [], completions = [], onPlan }) {
  const nav = useNavigate();
  const ranked = useMemo(() => rankSuggestions(tasks, completions), [tasks, completions]);
  const doneStrands = useMemo(() => {
    const doneIds = new Set(completions.map((c) => c && c.taskId).filter(Boolean));
    return new Set(tasks.filter((t) => doneIds.has(t.id)).map((t) => t.strand));
  }, [tasks, completions]);

  const [idx, setIdx] = useState(0);
  const task = ranked.length ? ranked[idx % ranked.length] : null;

  if (!task) {
    return (
      <Card className="tw tw--empty">
        <p className="tw__eyebrow">This week</p>
        <h2 className="tw__title tw__title--done">You've explored everything on offer 🌱</h2>
        <Note tone="brand">
          Fresh missions arrive each term — nothing to chase in the meantime. Revisit any
          favourite below; the good ones are worth doing twice.
        </Note>
      </Card>
    );
  }

  const taskPath = `/grown-ups/child/${childId}/task/${task.id}`;

  function reason() {
    if (task.strand && !doneStrands.has(task.strand)) {
      const label = STRAND_META[task.strand]?.label || "maths";
      return `A fresh ${label} adventure to explore.`;
    }
    const m = task.estimatedMinutes;
    if (Number.isFinite(m) && m <= 15) return `A quick one — about ${m} minutes together.`;
    if (Number.isFinite(m)) return `About ${m} minutes together.`;
    return "A gentle one to explore together.";
  }

  function commit() {
    if (onPlan) onPlan(task.id);
    nav(taskPath);
  }

  return (
    <Card className="tw">
      <div className="tw__top">
        <p className="tw__eyebrow">This week</p>
        <StrandBadge strand={task.strand} />
      </div>
      <h2 className="tw__title">
        <Link to={taskPath} className="tw__titlelink">
          {task.title || task.id}
        </Link>
      </h2>
      {task.hook && <p className="tw__hook">{task.hook}</p>}
      <p className="tw__reason">{reason()}</p>

      <div className="tw__actions">
        <Button variant="primary" onClick={commit}>
          We'll do this
        </Button>
        <Button variant="secondary" to={`${taskPath}/print`}>
          Print sheet
        </Button>
        {ranked.length > 1 && (
          <Button variant="ghost" onClick={() => setIdx((i) => (i + 1) % ranked.length)}>
            Show another
          </Button>
        )}
      </div>
    </Card>
  );
}

// ── styles ────────────────────────────────────────────────────────────────────
const TW_CSS = `
.tw{border:1px solid color-mix(in srgb,var(--brand) 26%,var(--line));
  background:linear-gradient(180deg,color-mix(in srgb,var(--brand) 7%,#fff),var(--card));}
.tw__top{display:flex;align-items:center;justify-content:space-between;gap:.6rem;flex-wrap:wrap;}
.tw__eyebrow{margin:0;font-size:.78rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--brand);}
.tw__title{margin:.4rem 0 .1rem;font-size:clamp(1.25rem,4vw,1.6rem);}
.tw__title--done{color:var(--brand-ink);}
.tw__titlelink{color:var(--brand-ink);text-decoration:none;}
.tw__titlelink:focus-visible{outline:2px solid var(--sky);outline-offset:2px;border-radius:6px;}
.tw__hook{margin:.25rem 0 .1rem;color:var(--ink);}
.tw__reason{margin:.15rem 0 .9rem;color:var(--ink-soft);font-size:.9rem;}
.tw__actions{display:flex;flex-wrap:wrap;gap:.6rem;}
.tw__actions .ui-btn{flex:0 1 auto;}
@media (hover:hover){.tw__titlelink:hover{text-decoration:underline;}}
`;

injectStyles("maths-thisweek-styles", TW_CSS);
